import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SITE, ABOUT_IMAGE } from "@/lib/site";
import useSEO from "@/hooks/useSEO";

const VALUES = [
  { t: "Craft over compromise", d: "Premium materials, precise install — no shortcuts on visible surfaces." },
  { t: "Brand-first thinking", d: "We design and install signage as a permanent extension of your brand." },
  { t: "Accountable timelines", d: "Clear scope, transparent quotes, and on-time delivery — every project." },
  { t: "Local to the GTA", d: "Site visits, measurements, and rapid in-person support across Toronto." },
];

export default function About() {
  useSEO({
    title: "About · Our Toronto Signage Studio",
    description: "Toronto Custom Signs & Wraps is a full-service signage and architectural vinyl studio in the GTA. 3M Certified Preferred Installer with 500+ delivered projects.",
    path: "/about",
    image: ABOUT_IMAGE,
  });
  return (
    <>
      <section className="pt-36 pb-20 bg-ink-950" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow gold-bar">About the studio</div>
            <h1 className="font-display text-5xl md:text-7xl mt-5 tracking-tight">We turn surfaces into brand statements.</h1>
            <p className="text-white/65 mt-7 max-w-2xl text-lg leading-relaxed">
              Toronto Custom Signs &amp; Wraps is a full-service signage and architectural vinyl studio
              based in the GTA. From small office branding to multi-floor commercial transformations,
              we handle design, production, and certified installation under one roof.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={ABOUT_IMAGE} alt="Toronto Custom Signs & Wraps studio — architectural wrap install in progress" className="aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[{n:"500+",l:"Projects delivered"},{n:"3M",l:"Certified installer"},{n:"7+",l:"Years in business"},{n:"GTA",l:"Service coverage"}].map((s,i)=>(
            <div key={i} data-testid={`stat-${i}`}>
              <div className="font-display text-5xl md:text-6xl text-gold">{s.n}</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 mt-3">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-28 bg-ink-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Principles</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">How we operate.</h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VALUES.map((v,i)=>(
              <div key={i} className="card-dark p-7" data-testid={`value-${i}`}>
                <Check size={20} className="text-gold mb-5"/>
                <h4 className="font-display text-xl">{v.t}</h4>
                <p className="text-white/55 text-sm mt-3 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3 className="font-display text-3xl md:text-4xl tracking-tight max-w-xl">Have a project? We'd love to scope it.</h3>
          <div className="flex gap-3">
            <Link to="/contact" className="btn-gold" data-testid="about-cta-quote">Get a quote <ArrowUpRight size={14}/></Link>
            <a href={SITE.phoneHref} className="btn-ghost">{SITE.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
